import { useCallback, useRef } from 'react'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'

import { useLenis } from '@/contexts/lenis'
import { useMotionMatchMedia, useMotionTouchMedia } from '@/hooks'
import { useLoaderStore } from '@/store/loader'

import { createLinesMotion, createWordsMotion, getScrollTriggerOptions, Motion } from './shared'

export const useHomeClinicMotion = () => {
  const motionRef = useRef<HTMLDivElement>(null)
  const revealScrollTrigger = useRef<ScrollTrigger>(null!)
  const cardsRevealScrollTrigger = useRef<ScrollTrigger>(null!)
  const parallaxScrollTrigger = useRef<ScrollTrigger>(null!)
  const fontsReady = useLoaderStore((state) => state.fontsReady)
  const { lenis } = useLenis()

  const titleMotion = useRef<Motion | null>(null)
  const paragraphMotion = useRef<Motion | null>(null)

  const revertSplitText = useCallback(() => {
    titleMotion.current?.revert()
    paragraphMotion.current?.revert()
  }, [])

  /**
   * reveal setup
   */
  useMotionTouchMedia(
    {
      pointer({ selector }) {
        if (!fontsReady || !lenis) return

        const rows = selector?.('[data-motion-row]')
        const title = selector?.('[data-motion-title]')[0]
        const paragraph = selector?.('[data-motion-paragraph]')[0]
        const button = selector?.('[data-motion-button]')[0]

        titleMotion.current = title ? createWordsMotion(title) : null
        paragraphMotion.current = paragraph ? createLinesMotion(paragraph) : null

        const animation = gsap.timeline({ onComplete: revertSplitText })
        titleMotion.current && animation.add(titleMotion.current.timeline, 0)
        paragraphMotion.current && animation.add(paragraphMotion.current.timeline, 0.1)
        button &&
          animation.from(button, { opacity: 0, y: 20, duration: 0.6, ease: 'power2.out' }, 0.3)

        revealScrollTrigger.current = ScrollTrigger.create({
          ...getScrollTriggerOptions({
            trigger: rows[0],
            animation
          })
        })
      },

      revert() {
        revealScrollTrigger.current?.kill()
        revertSplitText()
      }
    },
    { scope: motionRef, dependencies: [fontsReady, lenis] }
  )

  useMotionTouchMedia(
    {
      pointer({ selector }) {
        if (!fontsReady || !lenis) return

        const rows = selector?.('[data-motion-row]')
        // const cards = selector?.('[data-motion-card]')[0]
        // console.log(cards)

        const animation = gsap.timeline()

        cardsRevealScrollTrigger.current = ScrollTrigger.create({
          ...getScrollTriggerOptions({
            trigger: rows[0],
            animation
          })
        })
      },

      revert() {
        cardsRevealScrollTrigger.current?.kill()
      }
    },
    { scope: motionRef, dependencies: [fontsReady, lenis] }
  )

  /**
   * parallax setup
   */
  useMotionMatchMedia(
    {
      medium() {
        if (!lenis) return

        parallaxScrollTrigger.current = ScrollTrigger.create({
          trigger: motionRef.current,
          start: 'top bottom',
          end: 'bottom top',
          markers: false,
          scrub: true,
          animation: gsap.timeline({}).fromTo(
            '[data-motion-row]',
            { yPercent: (index) => (index === 0 ? 0 : index === 1 ? -20 : 0) },
            {
              yPercent: (index) => (index === 0 ? -50 : index === 1 ? 10 : 0),
              duration: 1,
              ease: 'none'
            }
          )
        })
      },

      revert() {
        parallaxScrollTrigger.current?.kill()
      }
    },
    { scope: motionRef, dependencies: [lenis] }
  )

  return { motionRef }
}
